import type { Theme } from '@mui/material/styles';
import { alpha } from '@mui/material/styles';

/** רוחב קבוע לפאנל שמאלי (תבניות / כלים) */
export const BERRY_PANEL_FIXED_WIDTH_PX = 480;
/**
 * רוחב ברירת מחדל לבר רשימת השיחות (px).
 * 420px — מאזן מול מרווחי ה־Grid ומצמצם רווח עודף מול התפריט הימני (לא תלוי ב־`BERRY_PANEL_FIXED_WIDTH_PX`).
 */
export const CHAT_LIST_PANEL_WIDTH_PX = 420;

/** ווידג'ט צף — עוזר תמיכה AI (סטנדרט Enterprise: ~480px) */
export const GLOBAL_APP_HELP_PANEL_WIDTH_PX = 480;
/** גובה מקסימלי לווידג'ט הצף בדסקטופ */
export const GLOBAL_APP_HELP_PANEL_MAX_HEIGHT_PX = 760;

/** עמידות זום / רזולוציה — רוחבים מינימליים לפאנלים (כלים / שיחות) וסף לצ'אט מרכזי */
export const LAYOUT_ZOOM_MIN_TOOLS_PANEL_PX = BERRY_PANEL_FIXED_WIDTH_PX;
export const LAYOUT_ZOOM_MIN_RIGHT_CHATS_PANEL_PX = CHAT_LIST_PANEL_WIDTH_PX;
/** כשהפאנל השמאלי פתוח והמרחב לצ'אט מתחת לסף — מוסתר פאנל ימין (מניעת חניקה) */
export const LAYOUT_ZOOM_CHAT_CENTER_COLLAPSE_RIGHT_BELOW_PX = 400;
/** רייל צף — שכבה מעל אזור הצ'אט (מתחת ל־AppBar/מודאלים גלובליים) */
export const CHAT_FLOATING_RAIL_Z_INDEX = 100;
/** גובה שורת הכלים העליונה בפיקסלים קבועים (לא rem) — מניעת נפיחות בזום דפדפן */
export const MAIN_HEADER_TOOLBAR_HEIGHT_PX = 64;
/**
 * חוזה כותרת משותף לשלושת עמודות הצ'אט (רשימת שיחות | חלון שיחה | פאנל צד / תבניות):
 * אותו ריפוד חיצוני, מרווח פנימי לפני קו המפריד וצבע מפריד אחיד — ללא כפילויות ב־sx מקומיים.
 * ערכי spacing ביחידות theme (ברירת מחדל 8px).
 */
export const CHAT_TRIPLEX_HEADER_OUTER_PY = 1.25;
/**
 * מרווח בין תוכן ה־Outlet לרייל הכלים הצף — קטן מ־`gridSpacing` כדי שלא ייווצר ריווח עודף מול קצה המסגרת.
 * ערך ביחידות `theme.spacing`.
 */
export const CHAT_OUTLET_TO_FLOATING_RAIL_SPACING = 1;
/** כמו `mb: 1` בשורת הזהות ב־`ChatHeaderUserData` — מרווח בין שורת הכלים לקו התחתון */
export const CHAT_TRIPLEX_HEADER_INNER_MARGIN_BOTTOM = 1;
/**
 * גובה אחיד לשדה חיפוש וכפתורי הפעולה בשורת רשימת השיחות — כמו `spacing(HEADER_TOOL_CONTROL_SPACING)` ב־`ChatHeaderUserData` (~44px).
 */
export const CHAT_USERS_HEADER_CONTROL_HEIGHT_PX = 44;
/** אייקונים בשורת כלי שיחה / רשימת שיחות — כמו ‎HEADER_LABEL_ICON_SX‎ בכותרת השיחה */
export const CHAT_HEADER_TOOLBAR_ICON_FONT_PX = 22;
/**
 * ריפוד אופקי פנימי לשורת כותרת פאנל הכלים (`ChatPanelHeaderTitleRow`) ולשורת פילטרי תבניות —
 * יחידות `theme.spacing`, מיושם ב־`style` (פיזי) כדי שלא יתהפך ב־RTL מול `sx`.
 */
export const CHAT_TOOLS_PANEL_ROW_HORIZONTAL_INSET_SPACING = 1.5;
/** מינימום גובה פס כותרת חלון השיחה (`#chat-header-user-data-box`) — שורת כלים ~64px + ריפוד חיצוני */
export const CHAT_TRIPLEX_CHAT_HEADER_MIN_HEIGHT_PX = 88;

/**
 * עמודת זהות בכותרת חלון השיחה (אווטאר · שם · טלפון) — רוחב מפורש ורספונסיבי (לא ברירות מחדל של flex).
 * מובייל: כמו וואטסאפ — רוב הרוחב לזהות, `minWidth: 0` לחיתוך; מקסימום יחסי למסך.
 * טאבלט/דסקטופ: עמודה צרה יותר (~פחות ממחצית הרוחב הקודם); ellipsis כשאין מקום.
 */
export const CHAT_HEADER_IDENTITY_COLUMN_WIDTH = {
  /** רוחב מזערי (px) — מסכים בינוניים+ */
  minPxMd: 116,
  /** רוחב מקסימלי (px) — דסקטופ (~פחות ממחצית הגרסה הקודמת) */
  maxPxLg: 248,
  /** ch מינימלי לבלוק השם (הערכה לשורה אחת) */
  nameMinCh: 6,
} as const;

export function chatTriplexHeaderBorderColor(theme: Theme): string {
  return theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.06)';
}

/** רקע כותרות רשימת שיחות / מקטעי פאנל צד תואמי רשימה */
export function chatTriplexListPanelHeaderBg(theme: Theme): string {
  return theme.palette.mode === 'dark' ? alpha(theme.palette.grey[900], 0.45) : '#F8F7FA';
}
/** מינימום רוחב לאזור הצ'אט המרכזי לפני גלילה אופקית של המעטפת */
export const LAYOUT_ZOOM_MAIN_CHAT_MIN_WIDTH_PX = 400;
/**
 * ברוחב מסך גדול — עמודת בועות ההודעות ממורכזת ומוגבלת לרוחב זה לקריאות (שורות לא ארוכות מדי).
 */
/** +60px יחסית לגרסה הקודמת — מקביל לרוחב שנלקח מרשימת השיחות לאזור ההיסטוריה */
export const CHAT_CONVERSATION_MESSAGES_COLUMN_MAX_WIDTH_PX = 860;
